import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


const TYPE = "MaturaBernoulliAtMostOneOpenTask";

const PROBABILITIES = [0.01, 0.02, 0.03, 0.05];
const TRIAL_COUNTS = [8, 10, 12];

const SCENARIOS = [
  {
    id: "agd",
    intro: ({ pText, n }) => (
      <>
        Sklep AGD prowadzi sprzedaż wysyłkową pralek. Prawdopodobieństwo
        uszkodzenia podczas transportu pralki wysłanej przez ten sklep do
        klienta jest równe <InlineMath math={decimalLatex(pText)} />.
      </>
    ),
    eventText: ({ n }) =>
      `spośród ${n} pralek wysłanych klientom przez ten sklep co najwyżej jedna ulegnie uszkodzeniu podczas transportu`,
    success: "pralka ulegnie uszkodzeniu podczas transportu",
    failure: "pralka nie ulegnie uszkodzeniu podczas transportu",
  },
  {
    id: "cream",
    intro: ({ pText, n }) => (
      <>
        W pewnym zakładzie mleczarskim śmietana produkowana jest w
        200-gramowych opakowaniach. Prawdopodobieństwo zdarzenia, że w losowo
        wybranym opakowaniu śmietana zawiera mniej niż 36% tłuszczu, jest równe{" "}
        <InlineMath math={decimalLatex(pText)} />. Kontroli poddajemy {n} losowo
        wybranych opakowań ze śmietaną.
      </>
    ),
    eventText: () =>
      "wśród opakowań poddanych tej kontroli będzie co najwyżej jedno opakowanie ze śmietaną, która zawiera mniej niż 36% tłuszczu",
    success: "opakowanie zawiera śmietanę z mniej niż 36% tłuszczu",
    failure: "opakowanie nie zawiera śmietany z mniej niż 36% tłuszczu",
  },
  {
    id: "fruit",
    intro: ({ pText, n }) => (
      <>
        Hurtownia owoców sprawdza losowo wybrane skrzynki jabłek.
        Prawdopodobieństwo, że w jednej skrzynce znajdują się owoce uszkodzone,
        jest równe <InlineMath math={decimalLatex(pText)} />. Do kontroli
        wybrano {n} skrzynek.
      </>
    ),
    eventText: () =>
      "co najwyżej jedna z wybranych skrzynek zawiera owoce uszkodzone",
    success: "skrzynka zawiera owoce uszkodzone",
    failure: "skrzynka nie zawiera owoców uszkodzonych",
  },
];

function rand(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function trimZeros(text) {
  return String(text).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
}

function decimalText(value, digits = 2) {
  return trimZeros(value.toFixed(digits)).replace(".", ",");
}

function decimalLatex(textOrNumber) {
  return String(textOrNumber).replace(".", ",").replace(",", "{,}");
}

function round3(value) {
  return Math.round(value * 1000) / 1000;
}

function resultText(value) {
  return value.toFixed(3).replace(".", ",");
}

function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "object" && value.target && "value" in value.target) {
    return String(value.target.value ?? "");
  }
  if (typeof value === "object") {
    for (const key of ["raw", "text", "value", "latex", "expr"]) {
      if (value[key] != null && typeof value[key] !== "object") {
        return String(value[key]);
      }
    }
  }
  return String(value);
}

function normalizeMath(raw) {
  return rawValue(raw)
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot|\\times|·|×/g, "*")
    .replace(/\{,\}/g, ".")
    .replace(/,/g, ".")
    .replace(/\s+/g, "")
    .replace(/p\s*\([^)]*\)\s*=/g, "")
    .replace(/^p=/, "")
    .replace(/^pa=/, "");
}

function parseNumber(raw) {
  let s = normalizeMath(raw)
    .replace(/^p=/, "")
    .replace(/^q=/, "")
    .replace(/[^0-9.\-+/]/g, "");
  if (!s) return null;
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  if (/^-?\d+(\.\d+)?\/-?\d+(\.\d+)?$/.test(s)) {
    const [a, b] = s.split("/").map(Number);
    return b === 0 ? null : a / b;
  }
  return null;
}

function expressionForEval(raw, task) {
  let s = normalizeMath(raw);
  s = s.replace(/\\binom\{0*([0-9]+)\}\{0\}/g, "1");
  s = s.replace(/\\binom\{0*([0-9]+)\}\{1\}/g, "$1");
  s = s.replace(/c\((\d+),(0|1)\)/g, (_, n, k) => (k === "0" ? "1" : n));
  s = s.replace(/\((\d+)po(0|1)\)/g, (_, n, k) => (k === "0" ? "1" : n));
  s = s.replace(/\^/g, "**");
  s = s.replace(/[{}]/g, "");
  s = s.replace(/p/g, String(task.p));
  s = s.replace(/q/g, String(task.q));
  if (!/^[0-9+\-*/().*]+$/.test(s)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const value = Function(`"use strict"; return (${s});`)();
    return Number.isFinite(value) ? value : null;
  } catch (_) {
    return null;
  }
}

function hasExpectedStructure(raw, task) {
  const s = normalizeMath(raw).replace(/[(){}]/g, "");
  const p = String(task.p);
  const q = String(task.q);
  const n = String(task.n);
  const nMinus = String(task.n - 1);

  const hasZeroTerm = s.includes(`${q}^${n}`) || s.includes(`${q}**${n}`);
  const hasOneTerm =
    s.includes(p) &&
    (s.includes(`${q}^${nMinus}`) || s.includes(`${q}**${nMinus}`)) &&
    (s.includes(`${n}*`) || s.includes(`*${n}`) || s.includes(`binom${n}1`));

  return hasZeroTerm && hasOneTerm;
}

function isExpressionCorrect(raw, task) {
  if (hasExpectedStructure(raw, task)) return true;
  const value = expressionForEval(raw, task);
  return value != null && Math.abs(value - task.probability) < 0.0005;
}

function toLatexExpression(raw) {
  const s = rawValue(raw).trim();
  if (!s) return "";
  return s
    .replace(/,/g, "{,}")
    .replace(/\*/g, "\\cdot ")
    .replace(/\^\(([^)]+)\)/g, "^{$1}")
    .replace(/\^(\d+)/g, "^{$1}")
    .replace(/sqrt\(([^)]+)\)/g, "\\sqrt{$1}");
}

export function generateTask() {
  const p = rand(PROBABILITIES);
  const n = rand(TRIAL_COUNTS);
  const scenario = rand(SCENARIOS);
  const q = Number((1 - p).toFixed(2));
  const probability = Math.pow(q, n) + n * p * Math.pow(q, n - 1);

  return {
    type: TYPE,
    points: 3,
    n,
    p,
    q,
    pText: decimalText(p),
    qText: decimalText(q),
    probability,
    rounded: round3(probability),
    roundedText: resultText(round3(probability)),
    scenarioId: scenario.id,
  };
}

function getScenario(task) {
  return SCENARIOS.find((s) => s.id === task.scenarioId) || SCENARIOS[0];
}

export function renderTask(task) {
  const scenario = getScenario(task);
  return (
    <div className="space-y-4">
      <p>
        <strong>Zadanie maturalne - otwarte (0-3 pkt).</strong>
      </p>
      <p>{scenario.intro({ pText: task.pText, n: task.n })}</p>
      <p>
        <strong>
          Oblicz prawdopodobieństwo zdarzenia A polegającego na tym, że {scenario.eventText({ n: task.n })}.
          Wynik zapisz w postaci ułamka dziesiętnego w zaokrągleniu do części tysięcznych.
          Zapisz obliczenia.
        </strong>
      </p>
    </div>
  );
}

function setField(answer, key, value, onChange) {
  onChange && onChange({ ...(answer || {}), [key]: value });
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  return (
    <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
      <div>
        <p>
          <strong>Krok 1 (1 pkt).</strong> Zapisz prawdopodobieństwo sukcesu i porażki w jednej próbie:
        </p>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
          <span><InlineMath math="p =" /></span>
          <EquationInput
            value={userAnswer.p || ""}
            onChange={(v) => setField(userAnswer, "p", v, onChange)}
            disabled={disabled}
            placeholder="np. 0,02"
            showPreview={false}
            basis="140px"
          />
          <span><InlineMath math="q =" /></span>
          <EquationInput
            value={userAnswer.q || ""}
            onChange={(v) => setField(userAnswer, "q", v, onChange)}
            disabled={disabled}
            placeholder="np. 0,98"
            showPreview={false}
            basis="140px"
          />
        </div>
      </div>

      <div>
        <p>
          <strong>Krok 2 (1 pkt).</strong> Zapisz działanie obliczające prawdopodobieństwo zdarzenia <InlineMath math="A" />:
        </p>
        <EquationInput
          value={userAnswer.expression || ""}
          onChange={(v) => setField(userAnswer, "expression", v, onChange)}
          disabled={disabled}
          placeholder="np. (0,98)^10+10*0,02*(0,98)^9"
          showPreview={true}
          basis="520px"
        />
      </div>

      <div>
        <p>
          <strong>Krok 3 (1 pkt).</strong> Podaj wynik po zaokrągleniu do części tysięcznych:
        </p>
        <EquationInput
          value={userAnswer.result || ""}
          onChange={(v) => setField(userAnswer, "result", v, onChange)}
          disabled={disabled}
          placeholder="np. 0,984"
          showPreview={false}
          basis="180px"
        />
      </div>
    </div>
  );
}

export function checkAnswer(task, userAnswer = {}) {
  const pUser = parseNumber(userAnswer.p);
  const qUser = parseNumber(userAnswer.q);
  const resultUser = parseNumber(userAnswer.result);

  const step1 =
    pUser != null &&
    qUser != null &&
    Math.abs(pUser - task.p) < 1e-9 &&
    Math.abs(qUser - task.q) < 1e-9;
  const step2 = isExpressionCorrect(userAnswer.expression, task);
  const step3 = resultUser != null && Math.abs(resultUser - task.rounded) < 0.0005;

  const parts = [
    { key: "pq", label: "p i q", correct: step1 },
    { key: "expression", label: "działanie Bernoulliego", correct: step2 },
    { key: "result", label: "wynik zaokrąglony", correct: step3 },
  ];
  const score = parts.filter((p) => p.correct).length;

  return {
    isCorrect: score === 3,
    correct: score === 3,
    score,
    maxScore: 3,
    parts,
    expected: {
      p: task.pText,
      q: task.qText,
      expression: `${task.qText}^${task.n} + ${task.n}*${task.pText}*${task.qText}^${task.n - 1}`,
      result: task.roundedText,
    },
  };
}

export function renderUserAnswer(userAnswer = {}) {
  return (
    <div className="space-y-1">
      <p>
        <InlineMath math={`p=${toLatexExpression(userAnswer.p) || "?"}`} />,{" "}
        <InlineMath math={`q=${toLatexExpression(userAnswer.q) || "?"}`} />
      </p>
      <p>
        <InlineMath math={`P(A)=${toLatexExpression(userAnswer.expression) || "?"}`} />
      </p>
      <p>
        <InlineMath math={`P(A)\\approx ${toLatexExpression(userAnswer.result) || "?"}`} />
      </p>
    </div>
  );
}

export function renderAnswer(task) {
  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong>{" "}
        <InlineMath math={`p=${decimalLatex(task.pText)},\\quad q=${decimalLatex(task.qText)}`} />
      </p>
      <p>
        <strong>Krok 2:</strong>{" "}
        <InlineMath
          math={`P(A)=(${decimalLatex(task.qText)})^{${task.n}}+${task.n}\\cdot ${decimalLatex(task.pText)}\\cdot (${decimalLatex(task.qText)})^{${task.n - 1}}`}
        />
      </p>
      <p>
        <strong>Krok 3:</strong>{" "}
        <InlineMath math={`P(A)\\approx ${decimalLatex(task.roundedText)}`} />
      </p>
    </div>
  );
}

export function renderExplanation(task) {
  const scenario = getScenario(task);
  return (
    <div className="space-y-4">
      <p>
        <strong>Krok 1.</strong> Traktujemy jako sukces zdarzenie: {scenario.success}.
        Porażką jest zdarzenie przeciwne: {scenario.failure}.
      </p>
      <BlockMath math={`p=${decimalLatex(task.pText)},\\qquad q=1-p=${decimalLatex(task.qText)}`} />

      <p>
        <strong>Krok 2.</strong> Korzystamy ze schematu Bernoulliego. Dla dokładnie
        <InlineMath math={"\\ k\\ "} /> sukcesów w <InlineMath math={`${task.n}`} /> próbach mamy:
      </p>
      <BlockMath math={`P(X=k)=\\binom{${task.n}}{k}p^kq^{${task.n}-k}`} />

      <p>
        Zdarzenie „co najwyżej jeden sukces” oznacza: zero sukcesów albo dokładnie jeden sukces.
      </p>
      <BlockMath
        math={`P(A)=P(X=0)+P(X=1)=\\binom{${task.n}}{0}q^{${task.n}}+\\binom{${task.n}}{1}p q^{${task.n - 1}}`}
      />
      <BlockMath
        math={`P(A)=(${decimalLatex(task.qText)})^{${task.n}}+${task.n}\\cdot ${decimalLatex(task.pText)}\\cdot (${decimalLatex(task.qText)})^{${task.n - 1}}`}
      />

      <p>
        <strong>Krok 3.</strong> Po obliczeniu i zaokrągleniu do części tysięcznych:
      </p>
      <BlockMath math={`P(A)\\approx ${decimalLatex(task.roundedText)}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
